import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as RemoveIcon } from 'assets/icons/remove.svg';
import './styles.scss';
import { Button } from '@cokitchen/cokitchen-components';
import { useNavigate } from 'react-router-dom';
import { ROUTE_KEYS } from 'utils/constants';
import SelectDropdown from 'components/base/select-dropdown';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import { getFullName } from 'utils/functions';
import useGiftMembersContext from 'contexts/gift-members.context';
import CurrencyInput from 'components/base/currency-input';
import { DepartmentType } from 'models/department.model';
import { MemberListType } from 'models/member.model';

const GiftMembers = () => {
  const mounting = useRef(true);
  const navigate = useNavigate();
  const [departments, setDepartments] = useState<DepartmentType[]>([]);
  const [members, setMembers] = useState<MemberListType[]>([]);
  const { activeCompany } = useDashboardContext();
  const { beneficiaries, setBeneficiaries } = useGiftMembersContext();

  const membersList = useMemo(() => {
    return members.map((m) => ({ id: m.user_id, name: getFullName(m.user) }));
  }, [members]);

  const getMembers = async () => {
    try {
      const res = await apiService.get('admin/upcoming-birthday');
      setMembers(res.data.data ?? []);
    } catch (err) {
      console.log(err);
    }
  };

  const getDepartments = async () => {
    if (!activeCompany.id) return;
    try {
      const res = await apiService.get(`department/company/${activeCompany.id}`);
      setDepartments(res?.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleInputChange = (
    field: 'department' | 'user_id' | 'amount',
    value: string,
    index: number
  ) => {
    const newBeneficiaries = beneficiaries.map((b) => ({ ...b }));
    newBeneficiaries[index][field] = value;
    setBeneficiaries(newBeneficiaries);
  };

  const handleDelete = (index: number) => {
    const newBeneficiaries = beneficiaries.filter((_, i) => i !== index);
    setBeneficiaries(newBeneficiaries);
  };

  useEffect(() => {
    if (mounting.current) {
      getMembers();
      getDepartments();
    }
    return () => {
      mounting.current = false;
    };
  }, []);

  useEffect(() => {
    setBeneficiaries(
      members.map((m) => ({
        name: getFullName(m.user),
        user_id: m.user_id,
        department: m.department.name,
        amount: ''
      }))
    );
  }, [members]);

  return (
    <div className='w-full max-w-[440px] mx-auto'>
      <p className='text-[32px] font-semibold text-[#2E3031] mb-1'>Gift Member(s)</p>
      <p className='text-[#6F7376]'>Appreciate your employees by gifting them</p>
      <div className='form py-6'>
        {beneficiaries.map((beneficiary, index) => (
          <div key={index} className='flex flex-col gap-4'>
            <div className='w-[390px]'>
              <SelectDropdown
                disabled
                label='Department'
                placeholder='Accounting'
                value={beneficiary.department}
                setValue={(v) => handleInputChange('department', v.name, index)}
                options={departments.map((d) => ({ id: d.id, name: d.name }))}
              />
            </div>
            <div className='w-full flex gap-5 items-center form_container mb-[28px]'>
              <div className='w-[390px] flex gap-4 form_box'>
                <div className='w-[50%]'>
                  <SelectDropdown
                    disabled
                    label='Member'
                    placeholder='Donatus globus'
                    value={beneficiary.name}
                    setValue={(v) => handleInputChange('user_id', v.id, index)}
                    options={membersList}
                  />
                </div>
                <div className='w-[50%] mt-2'>
                  <CurrencyInput
                    label='Gift amount'
                    placeholder='10,000'
                    value={beneficiary.amount}
                    onChange={(value) => handleInputChange('amount', value, index)}
                  />
                </div>
              </div>
              {index > 0 ? (
                <div className='rounded-[100px] border border-[#EAEBEB] bg-white shadow-md w-[30px] h-[30px] flex justify-center items-center cursor-pointer'>
                  <RemoveIcon onClick={() => handleDelete(index)} />
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      <div className='w-[390px]'>
        <Button
          variant='block'
          color='primary'
          className='w-[390px] h-[52px]'
          disabled={
            !beneficiaries.length || beneficiaries.some((b) => !Object.values(b).every(Boolean))
          }
          onClick={() => navigate(ROUTE_KEYS.GIFT_MEMBERS_CONFIRM)}>
          Continue
        </Button>
      </div>
    </div>
  );
};

export default GiftMembers;
